import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { formatAddress } from "helpers/address";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Instructions,
    Placeholder,
    SuggestionsList
} from "./AddressSuggestions.styled";

const Suggestion = dynamic(() => import("./suggestion/Suggestion"));
function AddressSuggestions({
    name,
    input,
    setAddress,
    suggestionIsOpen,
    setSuggestionIsOpen,
    suggestions,
    setSuggestions,
    toggleModal,
    cityOnly,
    instructions
}) {
    const suggestionListRef = useRef();
    const [lastSearched, setLastSearched] = useState("");
    const emptySuggestions = suggestions?.length === 0;
    const show = suggestionIsOpen;

    const handleSetAddress = address => {
        setAddress(address);
    };

    const isFieldMissing = address => {
        let fieldMissing = false;

        if (cityOnly) {
            return false;
        }

        Object.values(address).forEach(field => {
            if (field.length === 0) {
                fieldMissing = true;
            }
        });

        if (!address.houseNumber.match(/^\d+$/g)) {
            handleSetAddress(address);
            fieldMissing = true;
        }

        if (fieldMissing) {
            toggleModal(address);
        }

        return fieldMissing;
    };

    async function getPlaceDetails(id) {
        if (!id) {
            return null;
        }
        const response = await fetch(`/api/google-maps/details/${id}/`);
        return response.json();
    }

    const getSuggestions = useCallback(
        async searchQuery => {
            const types = cityOnly ? "&types=(cities)" : "";

            if (!searchQuery) {
                return null;
            }

            const response = await fetch(
                `/api/google-maps/autocomplete/?input=${searchQuery}${types}`
            );

            return response.json();
        },
        [cityOnly]
    );

    const onListItemClick = async suggestion => {
        setSuggestionIsOpen(false);

        if (suggestion?.address_components) {
            const formattedAddress = formatAddress(
                suggestion.address_components
            );

            isFieldMissing(formattedAddress);

            if (formattedAddress) {
                handleSetAddress(formattedAddress);
            }
        } else {
            const place = await getPlaceDetails(suggestion?.place_id);

            const formattedAddress = formatAddress(
                place?.result?.address_components,
                cityOnly
            );

            if (formattedAddress) {
                isFieldMissing(formattedAddress);
                if (formattedAddress) {
                    handleSetAddress(formattedAddress);
                }
            }
        }
    };

    const handleModalCallback = () => {
        setSuggestions([]);
        toggleModal();
    };

    const updateSuggestions = useCallback(
        async searchQuery => {
            const [street, postalCode, city, country] =
                searchQuery?.split(",") || [];
            const addressNotComplete =
                street && !postalCode && !city && !country;

            if (
                (cityOnly || addressNotComplete) &&
                searchQuery.length > 0 &&
                searchQuery !== lastSearched
            ) {
                const autocompleteData = await getSuggestions(searchQuery);

                if (!suggestionIsOpen && lastSearched !== "") {
                    setSuggestionIsOpen(true);
                }

                setLastSearched(searchQuery);

                if (autocompleteData?.predictions) {
                    setSuggestions(autocompleteData.predictions);
                }
            }
        },
        [
            cityOnly,
            lastSearched,
            getSuggestions,
            suggestionIsOpen,
            setSuggestionIsOpen,
            setSuggestions
        ]
    );

    useEffect(() => {
        const updateSuggestionsInput = async inputValue => {
            try {
                await updateSuggestions(inputValue);
                return Promise.resolve();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error updating suggestions", error);
                return Promise.reject();
            }
        };

        updateSuggestionsInput(input);
    }, [input, updateSuggestions]);

    return !show ? null : (
        <SuggestionsList
            emptySuggestions={emptySuggestions}
            inputFocus={suggestionIsOpen}
            ref={suggestionListRef}
            cityOnly={cityOnly}
        >
            {emptySuggestions && !cityOnly && (
                <Placeholder>
                    Begin met het typen van je straatnaam en huisnummer,
                    selecteer vervolgens jouw adres uit de lijst. Mocht je adres
                    er niet tussen staan kan je je adres handmatig invoeren.
                </Placeholder>
            )}

            {!emptySuggestions && instructions && (
                <li>
                    <Instructions cityOnly={cityOnly}>
                        {instructions}
                    </Instructions>
                </li>
            )}
            {!emptySuggestions &&
                suggestions.map(suggestion => (
                    <Suggestion
                        key={suggestion.place_id}
                        onClick={() => onListItemClick(suggestion)}
                        name={name}
                        description={
                            suggestion.description
                                ? suggestion.description
                                : suggestion.formatted_address
                        }
                        string={input}
                        cityOnly={cityOnly}
                    />
                ))}

            {!cityOnly && (
                <Suggestion
                    isLink
                    onClick={() => handleModalCallback()}
                    description="Voer jouw adres handmatig in"
                />
            )}
        </SuggestionsList>
    );
}

AddressSuggestions.propTypes = {
    name: PropTypes.string.isRequired,
    input: PropTypes.string,
    setAddress: PropTypes.func.isRequired,
    suggestionIsOpen: PropTypes.bool.isRequired,
    setSuggestionIsOpen: PropTypes.func.isRequired,
    suggestions: PropTypes.array.isRequired,
    setSuggestions: PropTypes.func.isRequired,
    toggleModal: PropTypes.func.isRequired,
    cityOnly: PropTypes.bool,
    instructions: PropTypes.string
};

AddressSuggestions.defaultProps = {
    input: "",
    cityOnly: false,
    instructions: ""
};

export default AddressSuggestions;
